// REPANCY-AGENT-LOCK: agent-ux J.3-hotfix (until 2026-05-21)
/**
 * J.3-hotfix — Client-side mirror of `convex/portalSlugRules.ts`.
 *
 * Original bug: SlugEditorBody used a `useEffect` on mount to call the
 * server `createPrivateWorkspaceJob` mutation just so it could read back
 * the default slug. That created a `portalProvisioningJobs` row with
 * `lastStepLabel: 'awaiting-slug-confirm'` before the user ever clicked
 * Continue — the upstream `getPrivateWorkspaceState` reactive query then
 * saw `hasPrivate: true, status: 'queued'` and re-rendered the panel as
 * `ProgressBody`, which the user couldn't escape from. The dangling
 * Convex row also blocked future opt-in attempts.
 *
 * The default slug is deterministic from the Clerk identity; there's no
 * need to ask the server for it. This module mirrors the SAME function
 * the Convex side exports so a future operator can rip it out by
 * deleting the Convex copy and pointing the server at this file (or
 * vice-versa). Both copies are vitested.
 *
 * The Convex side still validates the chosen slug on `confirmSlug` —
 * the client-side default is the visual seed only, not authoritative.
 *
 * KEEP IN SYNC with `convex/portalSlugRules.ts` — the implementations
 * MUST be byte-identical (modulo the lock comment + module header).
 */

/**
 * FNV-1a 32-bit hash truncated to 4 hex chars. Pure + deterministic.
 */
export function hashUserId(userId: string): string {
  let hash = 0x811c9dc5; // FNV offset basis (32-bit)
  for (let i = 0; i < userId.length; i++) {
    hash ^= userId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return ((hash >>> 0) & 0xffff).toString(16).padStart(4, '0');
}

/**
 * Normalize a name component to a slug-safe token.
 */
export function sanitizeNameToken(raw: string): string {
  if (typeof raw !== 'string') return '';
  const decomposed = raw.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  const lowered = decomposed.toLowerCase();
  const stripped = lowered.replace(/[^a-z0-9]+/g, '-');
  return stripped.replace(/^-+|-+$/g, '');
}

export interface SlugComponents {
  first: string;
  last: string;
  userId: string;
}

/**
 * Build the default slug for a freshly-signed-in user. Mirrors the
 * server-side `convex/portalSlugRules.ts::buildDefaultSlug`.
 */
export function buildDefaultSlug(components: SlugComponents): string {
  const suffix = hashUserId(components.userId);
  const first = sanitizeNameToken(components.first);
  const last = sanitizeNameToken(components.last);

  const parts: string[] = [];
  if (first.length > 0) parts.push(first);
  if (last.length > 0) parts.push(last);
  parts.push(suffix);

  let slug = parts.join('-');
  slug = slug.replace(/-+/g, '-').replace(/^-+|-+$/g, '');

  if (slug.length < 2 || slug === suffix) {
    return `lawyer-${suffix}`;
  }
  return slug;
}
